import type { Region } from "@/i18n";
import { OG_DEFAULT } from "@/seo/ogImages";

type LanguageBucket = "de" | "en";

interface MetaContent {
  title: string;
  description: string;
}

interface RouteMetaDefinition {
  localized: Record<LanguageBucket, MetaContent>;
  noindex?: boolean;
}

export type RouteMetaKey =
  | "home"
  | "about"
  | "abm"
  | "corporateInfluencer"
  | "linkedinAds"
  | "personalBranding"
  | "hubspotRevops"
  | "telemarketing"
  | "mailKampagne"
  | "motionAi"
  | "n8nAutomations"
  | "dmOutbound"
  | "ipTracking"
  | "einstiegsangebot"
  | "einstiegsangebotWelcome"
  | "booking"
  | "meetingConfirmed"
  | "meetingConfirmedExternal"
  | "webinare"
  | "webinarConfirmed"
  | "impressum"
  | "datenschutz"
  | "resources"
  | "blog"
  | "revenueBlueprint"
  | "caseStudies"
  | "caseStudyBSGroup"
  | "caseStudyNeXenio"
  | "caseStudySaasCrmPlattform"
  | "caseStudyCloud7"
  | "partnerOrders"
  | "notFound";

const ROUTE_META: Record<RouteMetaKey, RouteMetaDefinition> = {
  home: {
    localized: {
      de: {
        title: "LinkedIn Agentur für planbare B2B-Pipeline | Prometheus",
        description:
          "B2B-Leads über LinkedIn: Ads, Outbound & Telemarketing als System. Prometheus macht LinkedIn zum planbaren Revenue-Kanal.",
      },
      en: {
        title: "Prometheus Marketing | Predictable LinkedIn Revenue Channel",
        description:
          "Prometheus Marketing helps B2B teams turn LinkedIn into a predictable revenue channel with a repeatable pipeline.",
      },
    },
  },
  about: {
    localized: {
      de: {
        title: "Über uns: B2B-LinkedIn-Spezialisten DACH | Prometheus",
        description:
          "Das Team hinter Prometheus: planbare B2B-Pipeline über LinkedIn Ads, Outbound & Telemarketing im DACH-Raum.",
      },
      en: {
        title: "About Us | Prometheus Marketing",
        description:
          "Meet the team and approach behind Prometheus Marketing for B2B LinkedIn growth.",
      },
    },
  },
  abm: {
    localized: {
      de: {
        title: "Account-Based-Marketing (ABM) Agentur DACH | Prometheus",
        description:
          "ABM für den B2B-Mittelstand: Wunschkunden-Zielliste, Multi-Channel-Ansprache und telefonische Qualifizierung: Aus Zielaccounts wird Pipeline.",
      },
      en: {
        title: "Account Based Marketing | Prometheus Marketing",
        description:
          "Account-based marketing with ideal account targeting, personalized messaging, and measurable pipeline impact.",
      },
    },
  },
  corporateInfluencer: {
    localized: {
      de: {
        title: "Corporate-Influencer-Programm für B2B | Prometheus",
        description:
          "Mitarbeitende als glaubwürdige Markenbotschafter auf LinkedIn: strukturiert statt zufällig. Reichweite, die zu Gesprächen führt.",
      },
      en: {
        title: "Corporate Influencer Programs | Prometheus Marketing",
        description:
          "Turn internal experts into trusted corporate influencers that build authority and demand.",
      },
    },
  },
  linkedinAds: {
    localized: {
      de: {
        title: "LinkedIn Ads Agentur B2B (DACH) | Prometheus Marketing",
        description:
          "LinkedIn Ads, die qualifizierte Gespräche statt nur Klicks bringen: mit Targeting, Tracking und telefonischer Qualifizierung.",
      },
      en: {
        title: "LinkedIn Ads | Prometheus Marketing",
        description:
          "Performance-focused LinkedIn ads for qualified leads, efficient CPL, and predictable conversion rates.",
      },
    },
  },
  personalBranding: {
    localized: {
      de: {
        title: "Personal Branding auf LinkedIn für B2B | Prometheus",
        description:
          "Positionierung als Thought Leader auf LinkedIn: strategisches Profil, Content und Reichweite für Entscheider im B2B.",
      },
      en: {
        title: "B2B Personal Branding | Prometheus Marketing",
        description:
          "Build executive and expert visibility on LinkedIn to support trust, reach, and pipeline growth.",
      },
    },
  },
  hubspotRevops: {
    localized: {
      de: {
        title: "HubSpot-Implementierung & RevOps Agentur DACH | Prometheus",
        description:
          "HubSpot-Setup, Sales-Automation und Attribution aus einer Hand. Als zertifizierter Partner verbinden wir Marketing, Sales & Pipeline.",
      },
      en: {
        title: "HubSpot Implementation & RevOps Agency DACH | Prometheus",
        description:
          "HubSpot RevOps setup for clean processes, reliable reporting, and a scalable revenue engine.",
      },
    },
  },
  telemarketing: {
    localized: {
      de: {
        title: "B2B-Telemarketing & Leadqualifizierung DACH | Prometheus",
        description:
          "Qualifizierte Termine durch strukturierte telefonische Nachverfolgung: B2B-Telemarketing, das Leads in echte Sales-Gespräche verwandelt.",
      },
      en: {
        title: "B2B Telemarketing | Prometheus Marketing",
        description:
          "Strategic telemarketing as part of a modern outbound system for qualified meetings.",
      },
    },
  },
  mailKampagne: {
    localized: {
      de: {
        title: "B2B-E-Mail-Kampagnen mit Telefon-Follow-up | Prometheus",
        description:
          "E-Mail-Sequenzen mit integriertem telefonischem Nachfassen: mehr Antworten, mehr Termine, als Teil der Pipeline.",
      },
      en: {
        title: "Email Campaigns | Prometheus Marketing",
        description:
          "Structured email campaigns with relevant messaging and transparent performance tracking.",
      },
    },
  },
  motionAi: {
    localized: {
      de: {
        title: "Motion AI Einführung für Teams (DACH) | Prometheus",
        description:
          "Mehr Fokus durch KI: automatisierte Kalender- und Aufgabenplanung mit Motion AI, eingeführt und auf Ihr Team zugeschnitten.",
      },
      en: {
        title: "Motion AI Systems | Prometheus Marketing",
        description:
          "Automate revenue workflows with Motion AI and free your team for high-value sales work.",
      },
    },
  },
  n8nAutomations: {
    localized: {
      de: {
        title: "n8n-Automatisierung für B2B: voll gemanagt | Prometheus",
        description:
          "Workflow-Automatisierung mit n8n: sichere, skalierbare Tool-Integrationen, voll gemanagt, ohne eigenes Dev-Team.",
      },
      en: {
        title: "n8n Automations | Prometheus Marketing",
        description:
          "Connect your stack with n8n automations and remove manual steps from your revenue process.",
      },
    },
  },
  dmOutbound: {
    localized: {
      de: {
        title: "LinkedIn DM-Outbound für warme B2B-Erstgespräche | Prometheus",
        description:
          "Persönliche LinkedIn-Direktnachrichten, die Erstgespräche öffnen, ohne Spam. Outbound als Teil Ihres Revenue-Systems.",
      },
      en: {
        title: "DM Outbound | Prometheus Marketing",
        description:
          "Direct-message outbound with ICP signals, relevant personalization, and meeting-focused execution.",
      },
    },
  },
  ipTracking: {
    localized: {
      de: {
        title: "Website-Besucher identifizieren: IP-Tracking B2B | Prometheus",
        description:
          "Anonyme Website-Besucher DSGVO-konform identifizieren und Intent-Daten für ABM nutzen. So wird aus Traffic ein konkreter Zielaccount.",
      },
      en: {
        title: "B2B IP Tracking | Prometheus Marketing",
        description:
          "Identify high-intent companies early with IP tracking and prioritize outbound effectively.",
      },
    },
  },
  einstiegsangebot: {
    localized: {
      de: {
        title: "Pilotprojekt: Schwerpunkt nach Wahl + 100 Follower | Prometheus",
        description:
          "Ein Schwerpunkt nach Wahl, 250 ermittelte Zielunternehmen und 100 garantierte LinkedIn-Follower. 950 € zzgl. MwSt., bei Folgeprojekt voll angerechnet.",
      },
      en: {
        title: "Pilot project: focus of choice + 100 followers | Prometheus",
        description:
          "One focus of your choice, 250 identified target companies and 100 guaranteed LinkedIn followers. €950 plus VAT, fully credited to a follow-up project.",
      },
    },
  },
  einstiegsangebotWelcome: {
    localized: {
      de: {
        title: "Willkommen bei Prometheus | Buchung bestätigt",
        description: "Ihre Buchung ist eingegangen. Wir melden uns innerhalb von 24 Stunden.",
      },
      en: {
        title: "Willkommen bei Prometheus | Buchung bestätigt",
        description: "Ihre Buchung ist eingegangen. Wir melden uns innerhalb von 24 Stunden.",
      },
    },
    noindex: true,
  },
  booking: {
    localized: {
      de: {
        title: "Strategiegespräch buchen | Prometheus Marketing",
        description:
          "Kostenloses Strategiegespräch: Wir analysieren Ihr LinkedIn-Potenzial und wie daraus planbare Pipeline wird. Unverbindlich.",
      },
      en: {
        title: "Book a Meeting | Prometheus Marketing",
        description:
          "Book a strategy call with Prometheus Marketing and get a practical growth assessment.",
      },
    },
  },
  meetingConfirmed: {
    localized: {
      de: {
        title: "Termin Bestaetigt | Prometheus Marketing",
        description: "Ihr Termin ist bestaetigt. Wir freuen uns auf das Gespraech.",
      },
      en: {
        title: "Meeting Confirmed | Prometheus Marketing",
        description: "Your meeting is confirmed. We look forward to speaking with you.",
      },
    },
    noindex: true,
  },
  meetingConfirmedExternal: {
    localized: {
      de: {
        title: "Termin Bestaetigt | Prometheus Marketing",
        description: "Ihr Termin ist bestaetigt. Wir freuen uns auf das Gespraech.",
      },
      en: {
        title: "Meeting Confirmed | Prometheus Marketing",
        description: "Your meeting is confirmed. We look forward to speaking with you.",
      },
    },
    noindex: true,
  },
  webinare: {
    localized: {
      de: {
        title: "Kostenlose B2B-Webinare: LinkedIn & ABM live | Prometheus",
        description:
          "Alle 2 Wochen live: Brand-Ambassador-System und LinkedIn/Microsoft-Ads-ABM-Masterclass. Kostenlos anmelden und planbare B2B-Pipeline lernen.",
      },
      en: {
        title: "Free B2B Webinars: LinkedIn & ABM live | Prometheus",
        description:
          "Live every 2 weeks: Brand Ambassador System and the LinkedIn/Microsoft Ads ABM masterclass. Register for free and learn how to build a predictable B2B pipeline.",
      },
    },
  },
  webinarConfirmed: {
    localized: {
      de: {
        title: "Anmeldung bestätigt | Prometheus Marketing",
        description:
          "Ihre Anmeldung zum Live-Webinar ist bestätigt. Kalendereinladung und Zugangslink kommen per E-Mail.",
      },
      en: {
        title: "Registration Confirmed | Prometheus Marketing",
        description:
          "Your live webinar registration is confirmed. Your calendar invite and access link are on their way by email.",
      },
    },
    noindex: true,
  },
  impressum: {
    localized: {
      de: {
        title: "Impressum | Prometheus Marketing",
        description: "Impressum der Prometheus Marketing UG (haftungsbeschraenkt).",
      },
      en: {
        title: "Legal Notice | Prometheus Marketing",
        description: "Legal notice for Prometheus Marketing UG (haftungsbeschraenkt).",
      },
    },
  },
  datenschutz: {
    localized: {
      de: {
        title: "Datenschutz | Prometheus Marketing",
        description: "Datenschutzerklaerung und Hinweise zur Verarbeitung personenbezogener Daten.",
      },
      en: {
        title: "Privacy Policy | Prometheus Marketing",
        description: "Privacy policy and information about personal data processing.",
      },
    },
  },
  resources: {
    localized: {
      de: {
        title: "Kostenlose B2B-Marketing-Materialien & Vorlagen | Prometheus",
        description: "Vorlagen, Guides und Checklisten für planbares B2B-Wachstum: ABM, LinkedIn, RevOps. Kostenlos herunterladen.",
      },
      en: {
        title: "Free B2B Resources & Materials | Prometheus Marketing",
        description: "Guides, playbooks and templates for your B2B growth: free & ready to use. Curated by our founding team.",
      },
    },
  },
  blog: {
    localized: {
      de: {
        title: "B2B-Marketing Blog: LinkedIn, ABM & RevOps | Prometheus",
        description:
          "Meinungsstarke Artikel zu planbarer B2B-Pipeline: LinkedIn, ABM, Telemarketing, RevOps und IP-Tracking. Reichweite ≠ Pipeline.",
      },
      en: {
        title: "B2B Marketing Blog: LinkedIn, ABM & RevOps | Prometheus",
        description:
          "Opinionated articles on predictable B2B pipeline: LinkedIn, ABM, telemarketing, RevOps and IP tracking. Reach ≠ pipeline.",
      },
    },
  },
  revenueBlueprint: {
    localized: {
      de: {
        title: "Revenue Blueprint | Prometheus Marketing",
        description: "Ihr strukturiertes Deal-Dossier für planbare B2B-Pipeline: Setup, Skalierung und Revenue Operations.",
      },
      en: {
        title: "Revenue Blueprint | Prometheus Marketing",
        description: "Your structured deal dossier for predictable B2B pipeline: setup, scaling and revenue operations.",
      },
    },
  },
  caseStudies: {
    localized: {
      de: {
        title: "Case Studies: B2B-Pipeline-Projekte in DACH | Prometheus",
        description:
          "Echte Ergebnisse aus B2B-Pipeline-Projekten: wie LinkedIn, ABM und Telemarketing zu planbaren Terminen werden.",
      },
      en: {
        title: "Case Studies | Prometheus Marketing",
        description:
          "Concrete results from B2B pipeline projects across DACH, for specialized service firms with premium tickets and buyer committee sales.",
      },
    },
  },
  caseStudyBSGroup: {
    localized: {
      de: {
        title: "BS Group: 2,1 Mio. CHF Pipeline in 24 Monaten | Prometheus",
        description:
          "BS Group: 2,1 Mio. CHF Pipeline in 24 Monaten durch ABM, Corporate Influencer und einen 3-Termin-Sales-Prozess. Die Fall-Analyse.",
      },
      en: {
        title: "BS Group: 2,1 Mio. CHF Pipeline in 24 Monaten | Prometheus",
        description:
          "BS Group: 2,1 Mio. CHF Pipeline in 24 Monaten durch ABM, Corporate Influencer und einen 3-Termin-Sales-Prozess. Die Fall-Analyse.",
      },
    },
  },
  caseStudyNeXenio: {
    localized: {
      de: {
        title: "neXenio: 8-stellige Pipeline durch ABM | Prometheus",
        description:
          "neXenio: 8-stellige Pipeline aus 2.000 bis 5.000 Zielunternehmen. ABM, Corporate Influencer und HubSpot-Migration in der Fall-Analyse.",
      },
      en: {
        title: "neXenio: 8-stellige Pipeline durch ABM | Prometheus",
        description:
          "neXenio: 8-stellige Pipeline aus 2.000 bis 5.000 Zielunternehmen. ABM, Corporate Influencer und HubSpot-Migration in der Fall-Analyse.",
      },
    },
  },
  caseStudySaasCrmPlattform: {
    localized: {
      de: {
        title: "B2B-SaaS-CRM: 35 % geringere Kosten pro Trial | Prometheus",
        description:
          "B2B-SaaS-CRM-Plattform: 35 % geringere Kosten pro Trial durch zwei Webinar-Formate, ABM-Liste und LinkedIn-Push. Die Fall-Analyse.",
      },
      en: {
        title: "B2B-SaaS-CRM: 35 % geringere Kosten pro Trial | Prometheus",
        description:
          "B2B-SaaS-CRM-Plattform: 35 % geringere Kosten pro Trial durch zwei Webinar-Formate, ABM-Liste und LinkedIn-Push. Die Fall-Analyse.",
      },
    },
  },
  caseStudyCloud7: {
    localized: {
      de: {
        title: "Cloud7: HubSpot-Lizenzkosten mehr als halbiert | Prometheus",
        description:
          "Cloud7: HubSpot-Lizenzkosten mehr als halbiert durch den Wechsel auf Starter, dazu eine B2B-Reseller-Pipeline mit 2.500 Händlern.",
      },
      en: {
        title: "Cloud7: HubSpot-Lizenzkosten mehr als halbiert | Prometheus",
        description:
          "Cloud7: HubSpot-Lizenzkosten mehr als halbiert durch den Wechsel auf Starter, dazu eine B2B-Reseller-Pipeline mit 2.500 Händlern.",
      },
    },
  },
  partnerOrders: {
    localized: {
      de: {
        title: "Partnerbestellungen | Prometheus Marketing",
        description: "Interner Partnerbereich für Bestellungen.",
      },
      en: {
        title: "Partner Orders | Prometheus Marketing",
        description: "Internal partner ordering area.",
      },
    },
    noindex: true,
  },
  notFound: {
    localized: {
      de: {
        title: "Seite nicht gefunden | Prometheus Marketing",
        description: "Die angeforderte Seite wurde nicht gefunden.",
      },
      en: {
        title: "Page Not Found | Prometheus Marketing",
        description: "The requested page could not be found.",
      },
    },
    noindex: true,
  },
} satisfies Record<RouteMetaKey, RouteMetaDefinition>;

const REGION_TO_LOCALE: Record<Region, string> = {
  de: "de-DE",
  ch: "de-CH",
  en: "en",
};

function getLanguageBucket(region: Region): LanguageBucket {
  return region === "en" ? "en" : "de";
}

export function getSiteUrl(): string {
  const configuredSiteUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  if (configuredSiteUrl) {
    return configuredSiteUrl.replace(/\/$/, "");
  }

  return "";
}

export function getRegionLocale(region: Region): string {
  return REGION_TO_LOCALE[region];
}

export function resolveRouteMeta(routeKey: RouteMetaKey, region: Region) {
  const routeMeta = ROUTE_META[routeKey];
  const languageBucket = getLanguageBucket(region);
  const { title, description } = routeMeta.localized[languageBucket];

  return {
    title,
    description,
    noindex: routeMeta.noindex ?? false,
  };
}

export const SITE_FALLBACK_URL = "https://prometheus.marketing";
export const ORGANIZATION_LINKEDIN_URL =
  "https://www.linkedin.com/company/prometheus-marketing/";
/**
 * Re-Export für Altbestand. Die kanonische Quelle für OG-Motive ist
 * src/seo/ogImages.ts — dort steht die Route -> Bild-Map.
 */
export const DEFAULT_OG_IMAGE = OG_DEFAULT.image;

const SERVICE_ROUTE_KEYS = new Set<RouteMetaKey>([
  "abm",
  "corporateInfluencer",
  "linkedinAds",
  "personalBranding",
  "hubspotRevops",
  "telemarketing",
  "mailKampagne",
  "motionAi",
  "n8nAutomations",
  "dmOutbound",
  "ipTracking",
]);

export function isServiceRoute(routeKey: RouteMetaKey): boolean {
  return SERVICE_ROUTE_KEYS.has(routeKey);
}

export function getSiteUrlOrFallback(): string {
  return getSiteUrl() || SITE_FALLBACK_URL;
}

export function buildOrganizationJsonLd() {
  const base = getSiteUrlOrFallback();

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Prometheus Marketing",
    url: base,
    logo: `${base}/prometheus-logo.png`,
    sameAs: [ORGANIZATION_LINKEDIN_URL],
  };
}

export function buildServiceJsonLd(
  routeKey: RouteMetaKey,
  region: Region,
  canonicalUrl: string,
) {
  const { title, description } = resolveRouteMeta(routeKey, region);
  const base = getSiteUrlOrFallback();
  const serviceName = title.split("|")[0].trim();

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    description,
    url: canonicalUrl || base,
    areaServed: ["DE", "AT", "CH"],
    provider: {
      "@type": "Organization",
      name: "Prometheus Marketing",
      url: base,
    },
  };
}

interface BlogPostingJsonLdInput {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  authorName: string;
  authorUrl: string;
  authorImage?: string;
  url: string;
}

export function buildBlogPostingJsonLd(input: BlogPostingJsonLdInput) {
  const base = getSiteUrlOrFallback();

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: input.title,
    description: input.description,
    image: input.image,
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    author: {
      "@type": "Person",
      name: input.authorName,
      url: input.authorUrl,
      ...(input.authorImage ? { image: input.authorImage } : {}),
    },
    publisher: {
      "@type": "Organization",
      name: "Prometheus Marketing",
      logo: {
        "@type": "ImageObject",
        url: `${base}/prometheus-logo.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": input.url,
    },
  };
}

export function buildFaqPageJsonLd(faq: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

export function buildBreadcrumbJsonLd(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildBlogCollectionJsonLd(input: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    description: input.description,
    url: input.url,
    isPartOf: {
      "@type": "WebSite",
      name: "Prometheus Marketing",
      url: getSiteUrlOrFallback(),
    },
  };
}
